import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import CompanyUserCard from "./CompanyUserCard";
import AddCard from "./AddCard";
import UserModal from "./UserModal";
import { Box, Typography, Container } from "@mui/material";

const CompanyUsers = ({ currentCompanyUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("new");
  const [open, setOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [companyRoleKey, setCompanyRoleKey] = useState(null);
  useEffect(() => {
    const el =
      document.querySelector("[data-company-role]") ??
      document.querySelector(".react-app-router");
    setCompanyRoleKey(el?.dataset?.companyRole ?? null);
  }, []);

  useEffect(() => {
    fetch("/company/users.json")
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch users");
        return res.json();
      })
      .then((data) => {
        setUsers(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Fetch error:", err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <Box p={4} ml={4}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  return (
    <>
      <Container maxWidth="md" sx={{ mt: 4, mb: 10 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            担当者
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontSize: "24px", letterSpacing: "5px" }}
          >
            {users.length}/5
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 3,
          }}
        >
          {[...users]
            .sort((a, b) => a.id - b.id)
            .map((user) => (
              <CompanyUserCard
                key={user.id}
                user={user}
                currentCompanyUser={currentCompanyUser}
                setSelectedUser={setSelectedUser}
                allUsers={users}
                setOpen={setOpen}
                setMode={setMode}
                setUsers={setUsers}
                companyRoleKey={companyRoleKey}
              />
            ))}

          {/* adminのみ表示 */}
          {currentCompanyUser?.role === "admin" && users.length < 5 && (
            <AddCard
              onClick={() => {
                const generalIndex =
                  users.filter((u) => u.role === "general").length + 1;

                const newUser = {
                  last_name: "",
                  first_name: "",
                  email: "",
                  pr_text: "",
                  role: "general",
                  generalIndex,
                };

                setSelectedUser(newUser);
                setMode("new");
                setOpen(true);
              }}
            />
          )}
        </Box>

        <UserModal
          open={open}
          onClose={() => setOpen(false)}
          mode={mode}
          // 編集時は selectedUser を渡す／新規時は role 初期値のみ
          initialValues={selectedUser ?? { role: "general" }}
          currentCompanyUser={currentCompanyUser}
          allUsers={users}
          companyRoleKey={companyRoleKey}
          onSubmit={(fd, { mode, id }) => {
            if (mode === "edit") {
              const csrfToken = document.querySelector(
                'meta[name="csrf-token"]'
              )?.content;

              // ★ Promise を返す
              return fetch(`/company/users/${id}`, {
                method: "PATCH",
                headers: { "X-CSRF-Token": csrfToken },
                body: fd,
              })
                .then(async (res) => {
                  if (!res.ok) {
                    if (res.status === 422) {
                      const data = await res.json().catch(() => null);
                      if (data?.errors) {
                        // ← object / array どちらでも { errors: ... } で返す
                        return Promise.reject({ errors: data.errors });
                      }
                      return Promise.reject({
                        errors: ["バリデーションエラーが発生しました。"],
                      });
                    }
                    const text = await res.text();
                    throw new Error(
                      `Failed to update user: ${res.status}\n${text}`
                    );
                  }
                  return res.json();
                })
                .then((data) => {
                  setUsers((prev) =>
                    prev.map((user) => (user.id === data.id ? data : user))
                  );
                  setOpen(false);
                  toast.success("更新しました");
                })
                .catch((err) => {
                  // 422 は { errors: ... } で reject される想定
                  if (err?.errors) throw err;
                  console.error(err);
                  toast.error("更新に失敗しました");
                  throw err;
                });
            }

            if (mode === "new") {
              const csrfToken = document.querySelector(
                'meta[name="csrf-token"]'
              )?.content;

              // ★ Promise を返す
              return fetch("/company/users", {
                method: "POST",
                headers: { "X-CSRF-Token": csrfToken },
                body: fd,
              })
                .then(async (res) => {
                  if (!res.ok) {
                    if (res.status === 422) {
                      const data = await res.json().catch(() => null);
                      if (data?.errors) {
                        // ← object / array どちらでも { errors: ... } で返す
                        return Promise.reject({ errors: data.errors });
                      }
                      return Promise.reject({
                        errors: ["バリデーションエラーが発生しました。"],
                      });
                    }
                    const text = await res.text();
                    throw new Error(
                      `Failed to create user: ${res.status}\n${text}`
                    );
                  }
                  return res.json();
                })
                .then((data) => {
                  setUsers((prev) => [...prev, data]);
                  setOpen(false);
                  toast.success("担当者を登録しました。");
                })
                .catch((err) => {
                  if (err?.errors) throw err;
                  console.error(err);
                  toast.error("登録に失敗しました");
                  throw err;
                });
            }
          }}
        />
      </Container>
    </>
  );
};

export default CompanyUsers;
